import axios from "axios";

import constants from "../../constants";

/**
 * Custom domain of a Keepo, backed by the API's `/sites/{siteId}/custom-domain`
 * endpoints (GET/POST/POST verify/DELETE).
 */

export type DnsRecord = {
  type: "A" | "CNAME";
  name: string;
  value: string;
  /** Fully-qualified name the DNS lookup uses (`name` is the relative label the user types). */
  host?: string;
  verified: boolean;
  /** Set when the record resolves but to something that will never verify (e.g. a
   * Cloudflare-proxied record, see `CLOUDFLARE_PROXY_ISSUE`). */
  issue?: string | null;
};

export type CustomDomain = {
  domain: string;
  /** root: apex domain (A + www CNAME); subdomain: only a CNAME. Older API responses omit it (treat as root). */
  type?: "root" | "subdomain";
  includeWww?: boolean;
  provider: string;
  /** pending: records shown, waiting for the user; verifying: checking DNS; connected: all records found */
  status: "pending" | "verifying" | "connected";
  records: DnsRecord[];
  /** Mirrors records[].issue at the top level. */
  issues?: string[];
  /** A domain bought at Keepo: Keepo sets its DNS, so there's nothing for the person to add. */
  managedByKeepo?: boolean;
};

/** `DnsRecord.issue` value meaning the record exists but Cloudflare's proxy (orange cloud) is on,
 * so DNS resolves to Cloudflare's IPs instead of ours and verification never completes. */
export const CLOUDFLARE_PROXY_ISSUE = "CLOUDFLARE_PROXY";

/** Cloudflare's own docs on the proxy status / orange-vs-gray cloud toggle. */
export const CLOUDFLARE_PROXY_HELP_URL = "https://developers.cloudflare.com/dns/proxy-status/";

/** Lowercases and strips protocol, path and trailing dot: "https://WWW.Site.com.br/x" -> "www.site.com.br" */
export const normalizeDomain = (input: string) =>
  input
    .trim()
    .toLowerCase()
    .replace(/^[a-z]+:\/\//, "")
    .replace(/[/?#].*$/, "")
    .replace(/\.$/, "");

export const isValidDomain = (domain: string) =>
  /^(?=.{4,253}$)([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/.test(domain);

const baseUrl = (siteId: string) => `${constants.api}/sites/${siteId}/custom-domain`;

export const customDomainService = {
  async get(siteId: string): Promise<CustomDomain | undefined> {
    try {
      const response = await axios.get(baseUrl(siteId));
      return response?.data;
    } catch (e) {
      if (axios.isAxiosError(e) && e.response?.status === 404) {
        return undefined;
      }
      throw e;
    }
  },

  async connect(siteId: string, domain: string, provider: string): Promise<CustomDomain> {
    const response = await axios.post(baseUrl(siteId), { domain, provider });
    return response?.data;
  },

  /** Asks the server to re-check DNS (and advance nginx/certificate) once. */
  async verify(siteId: string): Promise<CustomDomain> {
    const response = await axios.post(`${baseUrl(siteId)}/verify`);
    return response?.data;
  },

  async disconnect(siteId: string): Promise<void> {
    await axios.delete(baseUrl(siteId));
  },
};
